import React from "react";

// ============================================================
// Card — Phase 1 shared card primitive.
// ============================================================
// A thin wrapper that applies the design-token contract from
// `/app/frontend/src/styles/tokens.css`:
//
//   • Base treatment: background var(--kc-surface), shadow +
//     radius from tokens; in dark mode the shadow is replaced
//     by a 1px var(--kc-border) outline (already handled by the
//     `.kc-card` base class in tokens.css).
//   • `variant` picks one of the semantic left-border classes
//     (`.card-{green|blue|amber|red|grey|purple}`) which drops
//     the left-side border-radius to 0 automatically.
//   • `variant="hero"` swaps the surface for the accent
//     gradient (`.kc-card-hero`).
//   • `compact` prop switches padding from --space-md to --space-sm.
//
// Colour semantics (per the design system):
//   green  → active member, present, completed, confirmed
//   blue   → scheduled event, calendar item, info, assigned slot
//   amber  → visitor, pending, warning, overdue, vacant slot
//   red    → absent, urgent, deletion, rejected, crisis
//   grey   → inactive, left, deceased, archived
//   purple → guest
//   hero   → gradient hero card (section heads, empty states)
//   none   → base .kc-card with no left border (default)
//
// Anything not tied to a semantic status should stay "none".
// Everything else uses the variant map to enforce consistency.
// ============================================================

const VARIANT_CLASS = {
  green: "card-green",
  blue: "card-blue",
  amber: "card-amber",
  red: "card-red",
  grey: "card-grey",
  purple: "card-purple",
  hero: "kc-card-hero",
  none: "",
};

export function Card({
  variant = "none",
  compact = false,
  className = "",
  children,
  as: Component = "div",
  ...rest
}) {
  const isHero = variant === "hero";
  const variantClass = VARIANT_CLASS[variant] ?? "";
  // Hero uses the gradient base; every other variant sits on top of
  // the standard `.kc-card` surface treatment.
  const baseClass = isHero ? "kc-card-hero" : "kc-card";
  const paddingClass = compact ? "kc-card-compact" : "";
  const combined = [baseClass, variantClass, paddingClass, className]
    .filter(Boolean)
    .join(" ");

  return (
    <Component className={combined} {...rest}>
      {children}
    </Component>
  );
}

export default Card;
